import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";

export const examAdminQuestionDetail = {
  pageTitle: "問題詳細",
  notFound: "対象の問題が見つかりません。",
  subTitleVersioned: "問題バージョンコード {versionedCode}",
  actions: {
    publish: "問題を公開",
    archive: "アーカイブ",
    viewing: "表示中",
    viewVersion: "このバージョンを表示",
  },
  tabs: {
    preview: "受験者ビュー",
    config: "問題タイプ設定",
    experience: "全情報",
    versions: "バージョン履歴",
    json: "構造化 JSON",
  },
  archiveConfirm: {
    title: "この問題をアーカイブしますか？",
    description: "アーカイブした問題は通常の一覧には表示されません。",
  },
  archivedAlert: {
    message: "この問題はアーカイブ済みです",
    description:
      "アーカイブ済みの問題は既定で読み取り専用表示になります。継続して編集する場合は、構造をコピーして新しい問題を作成するか、下書きに戻してから編集してください。",
  },
  sections: {
    basicInfo: "基本情報",
    versions: "バージョン履歴と差分",
    content: "問題内容と構造化データ",
  },
  versionDiff: {
    expandLabel: "バージョン履歴と差分を展開して表示",
    compareA: "比較バージョン A",
    compareB: "比較バージョン B",
    compareTitle: "比較：{from} -> {to}",
    scalarChanges: "基本項目変更（{count}）",
    jsonChanges: "JSON 項目変更（{count}）",
    rawJsonChanges: "JSON 構造変更（原文）（{count}）",
    pickDifferent: "異なる 2 つのバージョンを選択してください。",
    viewingCurrent: "現在表示中：",
    singleVersionLabel: "現在のバージョン：",
    singleVersionHint: "現在は 1 バージョンのみのため、比較できる履歴差分はありません。",
    backToLatest: "最新バージョンに戻る",
    snapshotTitle: "バージョンスナップショット：{code}",
    loadSnapshotFailed: "バージョンスナップショットの読み込みに失敗しました",
    summary: {
      total: "総変更 {count}",
      scalar: "基本項目 {count}",
      json: "JSON 項目 {count}",
      cases: "ケース変更 {count}",
    },
    readable: {
      title: "可読変更ビュー",
      stem: "問題文",
      starterCode: "テンプレートコード",
      publicCases: "公開ケース",
      hiddenCases: "非公開ケース",
      judgeConfig: "判定設定",
      analysis: "解説",
      metadata: "AI メタデータ",
      choiceSection: "選択式・正誤差分",
      shortAnswerSection: "記述式差分",
      environmentSection: "環境設定差分",
      starterCodeCompare: "テンプレートコード比較",
      caseChanges: "ケース差分詳細",
      caseScope: "ケース区分",
      caseNo: "ケース番号",
      caseField: "項目",
    },
  },
  overview: {
    type: "問題タイプ",
    status: commonShortNouns.status,
    currentVersion: "現在のバージョン",
    difficulty: "難易度",
    reviewStatus: "レビュー状態",
    mainCode: "問題コード",
    versionedCode: "問題バージョンコード",
    createdAt: commonShortFieldLabels.createdAt,
    latestVersionCreatedAt: "最新バージョン作成日時",
    internalNote: "メモ（管理者のみ表示）",
  },
  content: {
    candidateView: "受験者ビュー",
    typeConfig: "問題タイプ設定",
    structuredJson: "構造化 JSON",
    previewTitle: "問題プレビュー（受験者ビュー）",
    codeConfigTitle: "プログラミング問題設定",
    tryRunTitle: "オンライン試運転（管理者）",
    practicalConfigTitle: "実践問題設定（フォームビュー）",
    attackDefenseConfigTitle: "攻防コンテナ設定（フォームビュー）",
    adminExperienceTitle: "管理者体験（現在のセッション）",
    rawJsonContent: "content_json（原文）",
    rawJsonAnswer: "answer_json（原文）",
    rawJsonAnalysis: "analysis_json（原文）",
    rawJsonAi: "ai_metadata_json（原文）",
  },
  paperReferences: {
    title: "試験用紙参照",
    empty: "この問題はまだどの試験用紙からも参照されていません",
    total: "参照総数",
    paperCount: "試験用紙数",
    publishedPaperCount: "公開済み試験用紙",
    currentVersionRefs: "現行試験用紙版での参照",
    latestQuestionVersionRefs: "最新問題版での参照",
    latestPaper: "最新の参照試験用紙",
    viewAll: "すべての参照詳細を見る",
    drawerTitle: "試験用紙参照詳細（{count} 件）",
    table: {
      paper: "試験用紙",
      status: commonShortNouns.status,
      paperVersion: "試験用紙版",
      questionVersion: "問題版",
      position: "位置",
      score: "点数",
      actions: "操作",
    },
    labels: {
      current: "現行",
      history: "旧版",
      latest: "最新",
      outdated: "旧式",
    },
  },
  question: {
    taskTitle: "問題名",
    taskDescription: "問題説明",
    stem: "問題文",
    options: "選択肢",
    optionFallback: "選択肢 {index}",
    correctAnswer: "正答（管理者のみ表示）",
    analysis: "解説",
    referenceAnswer: "参考解答（管理者のみ表示）",
    keywords: "キーワード（管理者のみ表示）",
    keyPoints: "採点ポイント（管理者のみ表示）",
  },
  environment: {
    environmentType: "環境タイプ",
    environmentService: "環境サービス",
    resourceProvider: "リソースプロバイダー",
    checkMode: "チェックモード",
    labService: "Lab Service",
    image: "イメージ識別子",
    containerPort: "コンテナポート",
    accessHint: "アクセス説明",
    dynamicFlag: "動的 Flag",
    dynamicFlagOn: "有効",
    dynamicFlagOff: "無効",
    deliveryMode: "キャリア",
    staticCarrier: "静的問題キャリア",
    carrierAttachment: "添付ファイル",
    carrierContainer: "コンテナ",
    attachmentRefs: "添付ファイル参照",
    standardFlag: "標準 Flag（管理者のみ表示）",
    hint: "ヒント",
  },
  code: {
    supportedLanguages: "対応言語",
    defaultLanguage: "既定言語",
    timeLimit: "時間制限",
    memoryLimit: "メモリ制限",
    executionHint: "実行ヒント",
    starterCode: "テンプレートコード",
    starterCodeEmpty: "// テンプレートコードなし",
    publicCases: "サンプル / 公開ケース",
    caseTitle: "ケース {index}",
    publicCaseLabel: "公開ケース {index}",
    input: "入力",
    output: "出力",
    explanation: "説明",
    hiddenCaseCount: "非公開ケース数",
    hiddenCasesTitle: "非公開ケース（管理者のみ表示）",
    hiddenCaseTitle: "非公開ケース {index}",
    hiddenCaseLabel: "非公開ケース {index}",
  },
  tryRun: {
    runLanguage: "実行言語",
    sourceCode: "コード",
    tip: "インデントとシンタックスハイライトに対応しています。ショートカット：Ctrl/Cmd + Enter で実行。",
    runAllCases: "全ケース実行",
    running: "全ケースを実行中です",
    runningDescription: "しばらくお待ちください。完了後にこのダイアログ内で結果を表示します。",
    failed: "実行に失敗しました",
    summary: "全 {total} 件中 {passed} 件が成功",
    actualOutput: "実際の出力",
    saveAsNewVersion: "新バージョンとして保存（+1）",
    fillStarterCode: "テンプレートコードを入力",
    result: "結果：",
    rawDetail: "判定詳細（原文）",
    selectLanguage: "実行言語を選択してください",
    inputCode: "コードを入力してください",
    verdictContractAnomaly: "実行結果の契約異常：{value}",
    caseStatusContractAnomaly: "ケース状態の契約異常：{value}",
  },
  practical: {
    containerInstance: "コンテナインスタンス",
    instanceStatus: "インスタンス状態",
    webEndpoint: "Web エンドポイント",
    notCreated: "未作成",
    rerunInitScript: "初期化スクリプトを再実行",
    runCheckScript: "チェック用スクリプトを実行",
    initScript: "初期化スクリプト",
    checkScript: "チェック用スクリプト",
    initScriptEditable: "初期化スクリプト（変更後に再実行可能）",
    checkScriptEditable: "チェック用スクリプト（変更後に実行可能）",
    lastAction: "直近の操作：",
    lastActionInit: "初期化スクリプト",
    lastActionCheck: "チェック用スクリプト",
    attackDefenseCheckScriptNote:
      "攻防問題の体験ではチェック用スクリプトを使用せず、Flag を手入力して検証します。",
    flagValidation: "Flag 検証（管理者体験）",
    flagPlaceholder: "flag{...} を入力",
    validateFlag: "Flag を検証",
    lastValidation: "直近の検証結果：",
    validationCorrect: "正しい",
    validationIncorrect: "誤り",
    debugNote:
      "ここでスクリプトを直接編集してから環境を作成または再実行し、検証できます。現在の編集内容はデバッグ専用であり、問題設定には自動保存されません。",
    debugInitOnlyNote:
      "ここで初期化スクリプトを直接編集してから環境を作成または再実行し、検証できます。現在の編集内容はデバッグ専用であり、問題設定には自動保存されません。",
    initScriptDebug: "初期化スクリプト（デバッグ編集）",
    checkScriptDebug: "チェック用スクリプト（デバッグ編集）",
    callbackModeNoCheck:
      "現在は callback モードのため、通常はプラットフォーム側のチェック用スクリプトは不要です。",
  },
  messages: {
    updated: "問題を更新しました",
    updateSuccess: "問題を更新しました（内容が変更された場合のみ新しいバージョンを作成します）",
    deleteSuccess: "問題を削除しました",
    publishSuccess: "問題を公開しました",
    archiveSuccess: "問題をアーカイブしました",
    tryRunSuccess: "試運転が完了しました",
    tryRunSavedAsVersion: "新バージョン V{versionNo} として保存しました",
    envNotSupport: "現在の問題はコンテナ体験に対応していません",
    imageMissing: "現在の問題にはイメージ識別子が設定されていません",
    initScriptFailed: "初期化スクリプトの実行に失敗しました",
    sessionPersistFailed: "体験環境は作成されましたが、セッションの永続化に失敗しました",
    envCreateInitFailed: "体験環境は作成されましたが、初期化スクリプトの実行に失敗しました：{error}",
    envCreateInitSuccess: "体験環境を作成し、初期化スクリプトを実行しました：{status}",
    envCreateSuccess: "体験環境を作成しました",
    pleaseCreateEnv: "先に体験環境を作成してください",
    scriptEmptyInit: "初期化スクリプトが空です",
    scriptEmptyCheck: "チェック用スクリプトが空です",
    actionInit: "初期化",
    actionCheck: "チェック",
    actionSuccess: "{action}に成功しました",
    actionFailed: "{action}に失敗しました（{status}）：{detail}",
    checkScriptPassed: "チェック用スクリプトに合格しました（終了コード 0）",
    checkScriptFailed: "チェック用スクリプトに失敗しました（終了コード {exitCode}）",
    nonZeroExit: "スクリプトが 0 以外の終了コードを返しました",
    notAttackDefenseEnv: "現在はコンテナ体験可能な攻防問題ではありません",
    pleaseInputFlag: "先に flag を入力してください",
    dynamicFlagMissing: "コンテナ内で動的 flag 環境変数を読み取れませんでした：{envKey}",
    staticFlagMissing: "現在の静的 flag は設定されていません",
    flagPass: "Flag 検証に合格しました",
    flagWrong: "Flag が正しくありません。もう一度お試しください。",
    envDestroyed: "体験環境を破棄しました",
    sessionCleaned: "無効な体験環境セッションをクリーンアップしました。体験環境を再作成してください。",
  },
  confirmDelete: {
    title: "この問題を削除しますか？",
    description: "試験用紙や解答から参照されていない問題のみ削除できます。",
  },
  edit: {
    title: "問題を編集",
    note: "問題内容が変更された場合のみ新しいバージョンを作成し、バージョン番号を 1 つ増やします。下書き / 公開など状態だけの変更ではバージョンは変わりません。",
    statusLabel: "状態",
    saveChanges: "変更を保存",
    preview: "プレビュー",
    previewTitle: "問題プレビュー（受験者ビュー）",
  },
  table: {
    field: "項目",
    oldValue: "旧値",
    newValue: "新値",
    jsonField: "JSON 項目",
    path: "パス",
    changeType: "変更タイプ",
    versionNo: "バージョン",
    versionedCode: "問題バージョンコード",
    title: "タイトル",
    createdAt: commonShortFieldLabels.createdAt,
    actions: "操作",
  },
  diffChangeType: {
    added: "追加",
    removed: "削除",
    updated: "更新",
  },
  rawJsonTitle: "構造化 JSON",
} as const;
